/* ──────────────────────────────────────────────────────────────
 *  OutputShoreSlider — Shore A slider for the output panel
 *
 *  Visible only when a generated sample is selected and the
 *  bushing type supports output shore scaling.
 *  Range: 45–65. Triggers shore scaling via useShoreScaling hook.
 * ────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect } from 'react';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';

import { FieldsetGroup } from '@/components';
import { useSampleStore } from '@/stores/useSampleStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useInputStore } from '@/stores/useInputStore';
import { useShoreScaling } from '@/hooks/useShoreScaling';

const MARKS = [
  { value: 45, label: '45' },
  { value: 50, label: '50' },
  { value: 55, label: '55' },
  { value: 60, label: '60' },
  { value: 65, label: '65' },
];

const OutputShoreSlider: React.FC = () => {
  const bushingType = useBushingStore((s) => s.bushingType);
  const currentSelectedId = useSampleStore((s) => s.currentSelectedId);
  const genRowsById = useSampleStore((s) => s.genRowsById);
  const shoreBaselines = useSampleStore((s) => s.shoreBaselines);
  const { applyShoreScaling, captureBaseline, canScale } = useShoreScaling();

  // Single source of truth: shore A lives in the input store
  const inputShoreA = useInputStore((s) => s.shoreA);
  const setShoreA = useInputStore((s) => s.setShoreA);

  // Capture baseline values when a generated sample is first selected
  useEffect(() => {
    if (
      currentSelectedId &&
      currentSelectedId.startsWith('N') &&
      genRowsById[currentSelectedId] &&
      !shoreBaselines[currentSelectedId]
    ) {
      captureBaseline(currentSelectedId);
    }
  }, [currentSelectedId, genRowsById, shoreBaselines, captureBaseline]);

  // Sync output gen-row data whenever inputShoreA changes or a new baseline is captured.
  // This ensures the output panel always reflects the value chosen in the input panel.
  useEffect(() => {
    if (!currentSelectedId || !canScale) return;
    if (!shoreBaselines[currentSelectedId]) return;
    applyShoreScaling(inputShoreA);
  }, [shoreBaselines, inputShoreA, currentSelectedId, canScale, applyShoreScaling]);

  // Only visible for generated samples and non-BYD Voided types
  const visible =
    currentSelectedId !== null &&
    currentSelectedId.startsWith('N') &&
    bushingType !== 'BYD EV Voided';

  // Moving this slider writes back to the input store — single source of truth
  const handleChange = useCallback(
    (_: Event, value: number | number[]) => {
      const newVal = Array.isArray(value) ? value[0] : value;
      setShoreA(newVal);
    },
    [setShoreA],
  );

  if (!visible) return null;

  return (
    <Box sx={{ px: 1, mt: 1 }}>
      <FieldsetGroup legend="Shore A (ShA)">
        <Box sx={{ px: 1.5, pt: 0.5 }}>
          <Slider
            value={inputShoreA}
            min={45}
            max={65}
            step={5}
            marks={MARKS}
            valueLabelDisplay="auto"
            onChange={handleChange}
            size="small"
            color="info"
            sx={{ mt: 0.5 }}
          />
        </Box>
      </FieldsetGroup>
    </Box>
  );
};

export default React.memo(OutputShoreSlider);
